/**
 * Componente para renderizar cables (polylines) en el mapa
 * @module components/map/CableLines
 */

import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { Cable } from '@/types/fibers'
import { useFiberStore, useNodeStore, useUIStore, useIlluminationStore } from '@/stores'
import { cableOrigenId, cableDestinoId } from '@/utils/cableEndpoints'
import { cableLabelShort, cableLabelForSelect } from '@/utils/cableDisplayLabel'

interface CableLinesProps {
  map: L.Map
  cables: Cable[]
}

// Colores por tipo de cable
const CABLE_COLORS = {
  troncal: '#10b981', // Verde
  distribucion: '#3b82f6', // Azul
  acometida: '#f97316', // Naranja
  drop: '#a855f7', // Púrpura
} as const

// Pesos de línea por tipo
const CABLE_WEIGHTS = {
  troncal: 4,
  distribucion: 3,
  acometida: 2,
  drop: 1.5,
} as const

export function CableLines({ map, cables }: CableLinesProps) {
  const layerGroupRef = useRef<L.LayerGroup | null>(null)
  const nodes = useNodeStore((state) => state.nodes)
  const showCables = useUIStore((state) => state.showCables)
  const selectCable = useFiberStore((state) => state.selectCable)
  const selectedCableId = useFiberStore((state) => state.selectedCableId)
  const illuminatedPath = useIlluminationStore((state) => state.illuminatedPath)
  const isIlluminating = useIlluminationStore((state) => state.isIlluminating)

  useEffect(() => {
    if (!map || !showCables) {
      // Limpiar cables si no se deben mostrar
      if (layerGroupRef.current) {
        layerGroupRef.current.clearLayers()
      }
      return
    }

    // Crear o limpiar layer group
    if (!layerGroupRef.current) {
      layerGroupRef.current = L.layerGroup().addTo(map)
    } else {
      layerGroupRef.current.clearLayers()
    }

    // Crear un mapa de nodos por ID para búsqueda rápida
    const nodeMap = new Map(nodes.map((node) => [node.id, node]))

    // Renderizar cada cable
    cables.forEach((cable: any) => {
      const origenId = cableOrigenId(cable as any)
      const destinoId = cableDestinoId(cable as any)
      if (!origenId || !destinoId) return

      const origenNode = nodeMap.get(origenId)
      const destinoNode = nodeMap.get(destinoId)

      if (!origenNode || !destinoNode) {
        console.warn(`Cable ${cable.id}: nodo origen o destino no encontrado`, {origenId, destinoId})
        return
      }

      const isSelected = cable.id === selectedCableId
      const isIlluminated = isIlluminating && illuminatedPath?.cables?.includes(cable.id)
      const tipoLower = (cable.tipo || 'troncal').toLowerCase() as keyof typeof CABLE_COLORS
      const color = CABLE_COLORS[tipoLower] || CABLE_COLORS.troncal
      const weight = CABLE_WEIGHTS[tipoLower] || CABLE_WEIGHTS.troncal

      // Construir la ruta completa del cable
      let coordinates: [number, number][] = []

      // Si el cable tiene una ruta guardada con postes intermedios, usarla
      if (cable.ruta && Array.isArray(cable.ruta) && cable.ruta.length > 0) {
        coordinates = cable.ruta.map((punto: any) => [punto.lat, punto.lng] as [number, number])
      } else if (cable.postesIds && cable.postesIds.length > 0) {
        // Construir ruta desde postes intermedios
        coordinates.push([origenNode.lat, origenNode.lng])
        cable.postesIds.forEach((posteId: string) => {
          const poste = nodeMap.get(posteId)
          if (poste) {
            coordinates.push([poste.lat, poste.lng])
          }
        })
        coordinates.push([destinoNode.lat, destinoNode.lng])
      } else {
        // Ruta simple origen -> destino
        coordinates = [
          [origenNode.lat, origenNode.lng],
          [destinoNode.lat, destinoNode.lng],
        ]
      }

      // Determinar estilo del cable
      let lineColor: string = color
      let lineWeight = weight
      let lineOpacity = 0.7
      let className = ''
      
      if (isSelected) {
        lineColor = '#eab308' // Amarillo
        lineWeight = weight + 2
        lineOpacity = 1
      } else if (isIlluminated) {
        lineColor = '#00ff00' // Verde brillante para fibras iluminadas
        lineWeight = weight + 3
        lineOpacity = 1
        className = 'illuminated-cable'
      } else if (isIlluminating) {
        // Atenuar cables no iluminados cuando hay iluminación activa
        lineOpacity = 0.25
      }

      // Crear polyline con toda la ruta
      const polyline = L.polyline(
        coordinates,
        {
          color: lineColor,
          weight: lineWeight,
          opacity: lineOpacity,
          dashArray: cable.tipo === 'drop' && !isIlluminated ? '5, 5' : undefined,
          className: className,
        }
      )

      // Nombres de nodos para mostrar
      const origenName = (origenNode as any).nombre || origenNode.id.slice(-8)
      const destinoName = (destinoNode as any).nombre || destinoNode.id.slice(-8)
      const longitudKm = cable.longitudTotal ? (cable.longitudTotal / 1000).toFixed(2) : 'N/A'
      const numPostes = cable.postesIds?.length || 0
      const cableLabel = cableLabelShort(cable)

      // Tooltip con información del cable
      const tooltipContent = `
        <div class="text-xs">
          <strong>${cableLabel}</strong><br/>
          Tipo: ${cable.tipo}<br/>
          Fibras: ${cable.numeroFibras}<br/>
          ${origenName} → ${destinoName}
        </div>
      `
      polyline.bindTooltip(tooltipContent, {
        sticky: true,
      })

      // Popup con información detallada
      const popupContent = `
        <div class="p-3 min-w-[220px]">
          <div class="font-bold text-sm mb-2 flex items-center gap-2">
            <span style="color: ${color}">━━</span>
            ${cableLabelForSelect(cable)}
          </div>
          <div class="space-y-1.5 text-xs">
            <div class="flex justify-between">
              <span class="text-gray-500">Fibras:</span>
              <span class="font-medium">${cable.numeroFibras} FO</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Buffers:</span>
              <span class="font-medium">${cable.tieneBuffers ? Math.ceil(cable.numeroFibras / 12) : 0}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Longitud:</span>
              <span class="font-medium">${longitudKm} km</span>
            </div>
            ${numPostes > 0 ? `
            <div class="flex justify-between">
              <span class="text-gray-500">Postes de paso:</span>
              <span class="font-medium">${numPostes}</span>
            </div>
            ` : ''}
            <div class="pt-2 border-t mt-2">
              <div class="text-gray-500 mb-1">Conexión:</div>
              <div class="flex items-center gap-1">
                <span class="font-medium">${origenName}</span>
                <span class="text-gray-400">→</span>
                <span class="font-medium">${destinoName}</span>
              </div>
              ${numPostes > 0 ? `
              <div class="text-[10px] text-green-600 mt-1">
                ✓ Cable continuo (${numPostes} postes de paso)
              </div>
              ` : ''}
            </div>
          </div>
          <button
            onclick="window.handleCableSelect('${cable.id}')"
            class="mt-3 w-full px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
          >
            Ver Configuración
          </button>
        </div>
      `
      polyline.bindPopup(popupContent, {
        maxWidth: 250,
      })

      // Event handler para selección
      polyline.on('click', (e) => {
        L.DomEvent.stopPropagation(e)
        selectCable(cable.id)
      })

      // Agregar al layer group
      polyline.addTo(layerGroupRef.current!)
    })

    // Cleanup
    return () => {
      if (layerGroupRef.current) {
        layerGroupRef.current.clearLayers()
      }
    }
  }, [map, cables, nodes, showCables, selectedCableId, selectCable, illuminatedPath, isIlluminating])

  // Handler global para selección desde popup
  useEffect(() => {
    ;(window as any).handleCableSelect = (cableId: string) => {
      selectCable(cableId)
    }

    return () => {
      delete (window as any).handleCableSelect
    }
  }, [selectCable])

  return null // Este componente no renderiza nada directamente
}
